import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  FormControl, Grid, InputLabel, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import axios from "axios";
import QuickTrainerBulkPaste from "./QuickTrainerBulkPaste";
import QuickTrainerReceiptReview from "./QuickTrainerReceiptReview";
import TrainerBatchCostReview, { batchCostReady } from "./TrainerBatchCostReview";
import { readPendingQuickBulk } from "./quickTrainerBulkUtils";
import {
  createQuickTrainerIntakeRow, createTrainerIntakeBatch,
  fetchTrainerIntakeBatches, fetchTrainerIntakeRows,
} from "./trainerInventoryApi";
import {
  createQuickRequestId, isExactTrainerCard, exactTrainerEligibilityReason, makeQuickReceiptPayload,
  persistPendingQuickReceipt, positiveQuantity, quickBatchTotals,
  readPendingQuickReceipt,
} from "./quickTrainerIntakeUtils";

const VARIANTS = ["Normal", "Reverse Holo", "Holo"];
const SOURCES = [
  { value: "bulk_lot", label: "Bulk lot" },
  { value: "standard", label: "Standard" },
  { value: "pack_rip", label: "Pack rip" },
  { value: "targeted_buy", label: "Targeted buy" },
];
const DEFAULT_DETAILS = {
  sourceType: "bulk_lot", unitCost: "", sourceReference: "", storageLocation: "", notes: "",
};
const getError = (error, fallback) => error?.response?.data?.error || fallback;

export default function QuickTrainerIntake({ onGoToEbayUpdate }) {
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [batchDraft, setBatchDraft] = useState({ batch_name: "", source_label: "" });
  const [batchBusy, setBatchBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsBusy, setRowsBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [card, setCard] = useState(null);
  const [variant, setVariant] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [details, setDetails] = useState(DEFAULT_DETAILS);
  const [showDetails, setShowDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(readPendingQuickReceipt);
  const [bulkPending, setBulkPending] = useState(() => Boolean(readPendingQuickBulk()));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [focusSearch, setFocusSearch] = useState(false);
  const searchRef = useRef(null);
  const saveInFlight = useRef(false);
  const justSavedRef = useRef(false);
  const batchInFlightRef = useRef(false);
  const selectedBatchIdRef = useRef(batchId);
  selectedBatchIdRef.current = batchId;

  const selectedBatch = batches.find((batch) => String(batch.id) === String(batchId)) || null;
  const reviewedBatchCost = batchCostReady(selectedBatch);
  const canStage = selectedBatch?.status === "open" && reviewedBatchCost && !pending && !bulkPending && !saving && !batchBusy;
  const totals = useMemo(() => quickBatchTotals(rows), [rows]);
  const canSubmit = canStage && isExactTrainerCard(card) && VARIANTS.includes(variant) &&
    positiveQuantity(quantity) !== null &&
    (details.unitCost !== "" && (Number.isFinite(Number(details.unitCost)) &&
      Number(details.unitCost) >= 0 && Number(details.unitCost) <= 999999.9999)) &&
    Number(details.unitCost || 0) * Number(quantity) <= 99999999.99;

  useEffect(() => {
    setDetails((previous) => ({ ...previous,
      unitCost: reviewedBatchCost ? Number(selectedBatch.batch_unit_cost).toFixed(4) : "",
    }));
  }, [batchId, selectedBatch?.batch_cost_version, reviewedBatchCost]);

  const refreshBatches = async (preferredId) => {
    const response = await fetchTrainerIntakeBatches();
    const fetched = Array.isArray(response.data) ? response.data : [];
    setBatches(fetched);
    setBatchId((current) => {
      const requested = preferredId || (pending ? pending.batchId : current);
      if (requested && fetched.some((item) => String(item.id) === String(requested))) return requested;
      return fetched.find((item) => item.status === "open")?.id || "";
    });
    return fetched;
  };

  useEffect(() => {
    let cancelled = false;
    fetchTrainerIntakeBatches().then((response) => {
      if (cancelled) return;
      const available = Array.isArray(response.data) ? response.data : [];
      setBatches(available);
      const recoveryBatchId = pending?.batchId || readPendingQuickBulk()?.batchId;
      setBatchId(available.find((item) => String(item.id) === String(recoveryBatchId))?.id ||
        available.find((item) => item.status === "open")?.id || "");
    }).catch((requestError) => {
      if (!cancelled) setError(getError(requestError, "Unable to load trainer intake batches."));
    });
    return () => { cancelled = true; };
    // Initial selection intentionally uses the pending operation from storage once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRows([]);
    if (!batchId) {
      setRowsBusy(false);
      return undefined;
    }
    setRowsBusy(true);
    fetchTrainerIntakeRows(batchId).then((response) => {
      if (!cancelled) setRows(response.data?.rows || []);
    }).catch((requestError) => {
      if (!cancelled) setError(getError(requestError, "Unable to load selected intake batch."));
    }).finally(() => { if (!cancelled) setRowsBusy(false); });
    return () => { cancelled = true; };
  }, [batchId]);

  // Card search is read-only. Every result still requires a deliberate exact-print selection.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2 || pending || card) {
      setResults([]);
      setLookupBusy(false);
      setLookupError("");
      return undefined;
    }
    let cancelled = false;
    setLookupBusy(true);
    setLookupError("");
    const timer = setTimeout(() => {
      axios.get("/api/cards/lookup", { params: { q: term, limit: 24, high_only: 0 } })
        .then((response) => { if (!cancelled) setResults(response.data?.data || []); })
        .catch((requestError) => {
          if (!cancelled) {
            setResults([]);
            setLookupError(getError(requestError, "Card catalogue search failed."));
          }
        }).finally(() => { if (!cancelled) setLookupBusy(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, pending, card]);

  useEffect(() => {
    if (focusSearch && !saving && !pending) {
      searchRef.current?.focus();
      setFocusSearch(false);
    }
  }, [focusSearch, saving, pending]);

  const reloadRows = async (selectedId = batchId) => {
    if (!selectedId) return;
    try {
      const response = await fetchTrainerIntakeRows(selectedId);
      if (String(selectedId) === String(selectedBatchIdRef.current)) setRows(response.data?.rows || []);
    } catch (requestError) {
      setError(getError(requestError, "Could not refresh the saved-row list. Receipt status may still be confirmed."));
    }
  };

  const createBatch = async () => {
    if (!batchDraft.batch_name.trim() || batchInFlightRef.current || batchBusy || saving || pending) return;
    batchInFlightRef.current = true;
    setBatchBusy(true);
    setError("");
    setSuccess("");
    try {
      const response = await createTrainerIntakeBatch({
        batch_name: batchDraft.batch_name.trim(), source_label: batchDraft.source_label.trim(),
      });
      const nextId = response.data?.batch?.id;
      if (!nextId) throw new Error("Batch creation was not confirmed. Refresh batches before trying again.");
      setBatchDraft({ batch_name: "", source_label: "" });
      await refreshBatches(nextId);
      setSuccess(`Batch #${nextId} created. Ready to stage cards.`);
    } catch (requestError) {
      setError(getError(requestError, requestError.message || "Batch creation failed. Check the batch list before retrying."));
    } finally {
      batchInFlightRef.current = false;
      setBatchBusy(false);
    }
  };

  const handleSearchChange = (value) => {
    setQuery(value);
    setCard(null);
    setResults([]);
    setError("");
    setSuccess("");
  };

  const chooseCard = (selected) => {
    if (!isExactTrainerCard(selected)) return;
    justSavedRef.current = false;
    setCard(selected);
    setQuery(selected.name_and_number);
    setResults([]);
    setLookupError("");
    setError("");
    setSuccess("");
  };

  const setPendingReceipt = (value) => {
    // Write before any network request, so an interrupted or reloaded response can be retried.
    persistPendingQuickReceipt(value);
    setPending(value);
  };

  const submitReceipt = async (receipt) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await createQuickTrainerIntakeRow(receipt.batchId, receipt.payload);
      const confirmed = response.data?.success === true &&
        response.data?.row?.id &&
        response.data?.request_id === receipt.payload.request_id;
      if (!confirmed) throw new Error("The response did not confirm this exact receipt. Retry the same request.");
      justSavedRef.current = true;
      setPendingReceipt(null);
      const savedRow = response.data.row;
      if (String(receipt.batchId) === String(batchId)) {
        setRows((current) => current.some((item) => String(item.id) === String(savedRow.id))
          ? current.map((item) => String(item.id) === String(savedRow.id) ? savedRow : item)
          : [...current, savedRow]);
      }
      setCard(null);
      setQuery("");
      setResults([]);
      setVariant("");
      setQuantity("1");
      setSuccess(savedRow.voided_at
        ? `Original request #${savedRow.id} was removed after review. No new cards were staged; check the batch before submitting a new receipt.`
        : `${response.data.replayed ? "Already saved (current reviewed row)" : "Staged"}: ${savedRow.quantity} × ${savedRow.card_name} (${savedRow.card_variant}). No physical stock or listings updated.`);
      setFocusSearch(true);
      // Refresh totals independently; a failed GET must not turn a confirmed POST into a retry.
      reloadRows(receipt.batchId);
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status >= 400 && status < 500) {
        // The server rejected this operation; it was not accepted as a receipt.
        setPendingReceipt(null);
        setError(getError(requestError, "This receipt was rejected. Check the card/variant and try again."));
      } else {
        setError(`${getError(requestError, requestError.message || "Network or server error")}. Save not confirmed. Retry the same request ID; do not add the cards again as a new receipt.`);
      }
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const addCard = (event) => {
    event.preventDefault();
    if (!canSubmit || saveInFlight.current || justSavedRef.current) return;
    const receipt = {
      batchId,
      payload: makeQuickReceiptPayload({
        card, variant, quantity: positiveQuantity(quantity),
        details, requestId: createQuickRequestId(),
      }),
    };
    setPendingReceipt(receipt);
    submitReceipt(receipt);
  };

  const retryPending = () => {
    if (!pending || saveInFlight.current) return;
    if (String(batchId) !== String(pending.batchId)) setBatchId(pending.batchId);
    submitReceipt(pending);
  };

  const discardPending = () => {
    if (saveInFlight.current) return;
    if (!window.confirm("This request may already be saved. Check the selected batch before discarding its retry ID. Discard pending request?")) return;
    setPendingReceipt(null);
    setError("");
    setSuccess("Pending retry discarded. Verify the batch rows before submitting these physical cards again.");
  };

  return (
    <Stack spacing={2} sx={{ pb: 3 }}>
      <Alert severity="info">
        <strong>Playable Card Intake.</strong> Identify exact prints and finishes, paste multiple photo-derived rows,
        review batch COGS, then explicitly confirm physical receipt below. Staging does not change physical or eBay stock.
        Received playable cards are recorded as Near Mint (NM). eBay quantity changes require separate verification and explicit approval; physical allocation is not required for ordinary intake.
      </Alert>

      <Card variant="outlined"><CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">1. Select batch and confirm unit cost</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <FormControl fullWidth size="small" sx={{ maxWidth: { md: 480 } }}>
              <InputLabel id="quick-batch-label">Intake batch</InputLabel>
              <Select labelId="quick-batch-label" label="Intake batch" value={batchId}
                disabled={saving || Boolean(pending) || bulkPending || batchBusy}
                onChange={(event) => { setBatchId(event.target.value); setError(""); setSuccess(""); }}>
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    #{batch.id} {batch.batch_name} ({batch.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" disabled={saving || batchBusy} onClick={() => {
              setError("");
              refreshBatches().then(() => reloadRows()).catch((e) => setError(getError(e, "Refresh failed.")));
            }}>Refresh batch</Button>
            {selectedBatch && <Chip size="small" color={selectedBatch.status === "open" ? "info" : "default"} label={selectedBatch.status === "open" ? "Open for staging" : "Finalised / read-only"} />}
          </Stack>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="New batch name" value={batchDraft.batch_name}
                disabled={batchBusy || saving || Boolean(pending) || bulkPending}
                onChange={(event) => setBatchDraft((previous) => ({ ...previous, batch_name: event.target.value }))}
                placeholder="e.g. Trainer Box 1" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Source label (optional)" value={batchDraft.source_label}
                disabled={batchBusy || saving || Boolean(pending) || bulkPending}
                onChange={(event) => setBatchDraft((previous) => ({ ...previous, source_label: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="outlined" onClick={createBatch}
                disabled={!batchDraft.batch_name.trim() || batchBusy || saving || Boolean(pending) || bulkPending}>
                {batchBusy ? "Creating…" : "Create batch"}
              </Button>
            </Grid>
          </Grid>
          {!selectedBatch && <Alert severity="warning">Select an open batch or create a new one to start.</Alert>}
          {selectedBatch && !reviewedBatchCost && <Alert severity="warning">Confirm the new batch unit COGS below before staging any cards.</Alert>}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Entries: ${totals.entries}`} />
            <Chip label={`Distinct prints/finishes: ${totals.distinct}`} />
            <Chip color="warning" label={`Staged, not received: ${totals.staged}`} />
            <Chip color="success" label={`Physically received: ${totals.received}`} />
            <Chip label={`Total cards: ${totals.total}`} />
            <Chip label={`Removed rows: ${totals.voidedRows}`} />
            {rowsBusy && <CircularProgress size={18} aria-label="Loading rows" />}
          </Stack>
        </Stack>
      </CardContent></Card>

      {pending && (
        <Alert severity="warning">
          <Stack spacing={1}>
            <Typography fontWeight={600}>Previous save has not been confirmed</Typography>
            <Typography variant="body2">
              Batch #{pending.batchId}: {pending.payload?.quantity} × {pending.payload?.name_and_number} ({pending.payload?.card_variant}).
              Retry uses the same request ID and cannot create a second receipt for the same operation.
            </Typography>
            <Typography variant="caption">Request ID: {pending.payload?.request_id}</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" disabled={saving} onClick={retryPending}>Retry same save</Button>
              <Button variant="outlined" size="small" disabled={saving} onClick={discardPending}>Discard retry after checking batch</Button>
            </Stack>
          </Stack>
        </Alert>
      )}
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      {success && <Alert severity="success" role="status">{success}</Alert>}

      {selectedBatch && <Alert severity={reviewedBatchCost ? "success" : "warning"} role="status">
        Batch #{selectedBatch.id} unit cost: {reviewedBatchCost ? `AU$${Number(selectedBatch.batch_unit_cost).toFixed(4)} (reviewed, version ${selectedBatch.batch_cost_version})` : "Not confirmed — staging blocked"}.
        Cost changes apply to the batch under Rule A; review before saving cards.
      </Alert>}

      <TrainerBatchCostReview batch={selectedBatch} disabled={saving || bulkPending || Boolean(pending) || batchBusy}
        onSaved={async (selectedId) => { await refreshBatches(selectedId); await reloadRows(selectedId); }} />

      <Typography variant="h6">2. Paste photo identification results or add an exact card</Typography>
      <Typography variant="body2" color="text.secondary">
        Codex can identify cards from photos outside this application and paste its structured rows here.
        Preview the matches before committing; repeated photos must not be committed twice.
      </Typography>
      <QuickTrainerBulkPaste batchId={batchId} batchOpen={selectedBatch?.status === "open"}
        details={details} costReviewed={reviewedBatchCost} disabled={saving || batchBusy || Boolean(pending)}
        onPendingChange={setBulkPending} onCommitted={(confirmedBatchId) => {
          if (String(confirmedBatchId) !== String(batchId)) setBatchId(confirmedBatchId);
          else reloadRows(confirmedBatchId);
        }} />

      <Card variant="outlined"><CardContent>
        <Box component="form" onSubmit={addCard}>
          <Stack spacing={2}>
            <Typography variant="h6">3. Optional manual exact-card entry</Typography>
            <TextField inputRef={searchRef} fullWidth size="small" label="Find card — name, set or number"
              value={query} disabled={!canStage} onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Type at least 2 characters" autoComplete="off"
              helperText="Select the correct catalogue result. Similar names from different sets are not interchangeable." />
            {lookupBusy && <Typography variant="body2" color="text.secondary">Searching catalogue…</Typography>}
            {lookupError && <Alert severity="error">{lookupError}</Alert>}
            {!lookupBusy && query.trim().length >= 2 && !card && !lookupError && results.length === 0 &&
              <Alert severity="info">No exact print selected. If results are missing or ambiguous, refine the search. Do not guess.</Alert>}
            {!card && results.length > 0 && (
              <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: 290 }}>
                <Table size="small" stickyHeader aria-label="Card catalogue matches">
                  <TableHead><TableRow>
                    <TableCell>Card name / number</TableCell><TableCell>Set</TableCell><TableCell>Rarity</TableCell><TableCell align="right">Choose</TableCell>
                  </TableRow></TableHead>
                  <TableBody>{results.map((candidate) => (
                    <TableRow key={candidate.id || candidate.name_and_number}>
                      <TableCell>{candidate.name_and_number || "-"}</TableCell>
                      <TableCell>{candidate.set_name || "-"}</TableCell>
                      <TableCell>{candidate.rarity || "-"}</TableCell>
                      <TableCell align="right">
                        {exactTrainerEligibilityReason(candidate) &&
                          <Typography variant="caption" color="text.secondary" display="block" role="status">
                            {exactTrainerEligibilityReason(candidate)}
                          </Typography>}
                        <Button size="small" variant="outlined" disabled={!canStage || !isExactTrainerCard(candidate)}
                          onClick={() => chooseCard(candidate)}>
                          Select exact card
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </TableContainer>
            )}
            {card && (
              <Alert severity="info" action={<Button size="small" disabled={!canStage} onClick={() => { setCard(null); setQuery(""); }}>Change</Button>}>
                <Typography fontWeight={600}>Confirm selected print against the photograph</Typography>
                <Typography variant="body2">Card: {card.name_and_number}</Typography>
                <Typography variant="body2">Set: {card.set_name} · Catalogue ID: {card.id}</Typography>
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="quick-variant-label">Physical card finish</InputLabel>
                  <Select labelId="quick-variant-label" label="Physical card finish" value={variant}
                    disabled={!canStage} onChange={(event) => setVariant(event.target.value)}>
                    <MenuItem value=""><em>Select finish explicitly</em></MenuItem>
                    {VARIANTS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField size="small" fullWidth label="Cards received in photograph" type="number"
                  value={quantity} disabled={!canStage} inputProps={{ min: 1, step: 1 }}
                  onChange={(event) => setQuantity(event.target.value)} />
              </Grid>
            </Grid>
            <Button variant="text" size="small" sx={{ alignSelf: "flex-start" }} disabled={!canStage}
              onClick={() => setShowDetails((current) => !current)}>
              {showDetails ? "Hide receipt details" : "Show receipt details (source, cost, reference)"}
            </Button>
            {showDetails && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="quick-source-label">Source type</InputLabel>
                    <Select labelId="quick-source-label" label="Source type" value={details.sourceType}
                      disabled={!canStage}
                      onChange={(event) => setDetails((current) => ({ ...current, sourceType: event.target.value }))}>
                      {SOURCES.map((source) => <MenuItem key={source.value} value={source.value}>{source.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Saved batch unit cost (AUD)" value={details.unitCost}
                    InputProps={{ readOnly: true }} disabled helperText="Change this using Batch unit COGS above. Applies to every receipt in this batch." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Photo/source reference" value={details.sourceReference}
                    disabled={!canStage} onChange={(event) => setDetails((current) => ({ ...current, sourceReference: event.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Storage location" value={details.storageLocation}
                    disabled={!canStage} onChange={(event) => setDetails((current) => ({ ...current, storageLocation: event.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Notes" value={details.notes}
                    disabled={!canStage} onChange={(event) => setDetails((current) => ({ ...current, notes: event.target.value }))} />
                </Grid>
              </Grid>
            )}
            <Box>
              <Button type="submit" variant="contained" size="large" disabled={!canSubmit}>
                {saving ? "Saving…" : "Add card to staged batch"}
              </Button>
              <Typography color="text.secondary" variant="caption" display="block" sx={{ mt: 1 }}>
                Confirmed exact print, chosen finish and positive quantity required. Each deliberate Add creates a new receipt; retry an uncertain save using the retry control above.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </CardContent></Card>

      {selectedBatch?.status === "finalised" && <Alert severity="info">
        Stock received and COGS recorded. Open <strong>2. eBay Update</strong> to review received cards and their existing listing. Receiving never updates eBay. Routine physical allocation is not required; older tools remain in Archive / Legacy.
      </Alert>}

      {selectedBatch?.status === "finalised" && onGoToEbayUpdate && (
        <Box><Button variant="contained" onClick={onGoToEbayUpdate}>Continue to eBay Update</Button></Box>
      )}

      <Card variant="outlined"><CardContent>
        <QuickTrainerReceiptReview key={batchId || "none"} batchId={batchId} rows={rows}
          batchOpen={selectedBatch?.status === "open"}
          disabled={saving || batchBusy || Boolean(pending) || bulkPending || rowsBusy}
          reloadRows={() => reloadRows(batchId)}
          refreshBatches={() => refreshBatches(batchId)} />
      </CardContent></Card>
    </Stack>
  );
}
